import clsx from "clsx";
import { Edit2, MoreHorizontal } from "lucide-react";

export interface CategoryCardData {
  id: string;
  name: string;
  color: string;
  type: "expense" | "income";
  budget: number | null;
  spent: number;
}

interface CategoryCardProps {
  category: CategoryCardData;
  currency: string;
  isHe: boolean;
  onOpen: (categoryName: string) => void;
  onEdit: () => void;
  onMore: () => void;
}

export function CategoryCard({
  category,
  currency,
  isHe,
  onOpen,
  onEdit,
  onMore,
}: CategoryCardProps) {
  const fmt = (n: number) =>
    `${currency}${Math.round(Math.abs(n)).toLocaleString(isHe ? "he-IL" : "en-US")}`;

  const pctRaw = category.budget ? (category.spent / category.budget) * 100 : 0;
  const pct = Math.max(0, Math.round(pctRaw));
  const fillPct = Math.min(100, Math.max(0, pctRaw));
  const overBudget = !!category.budget && category.spent > category.budget;
  const leftAmount = category.budget ? Math.max(0, category.budget - category.spent) : 0;
  const overAmount = category.budget ? Math.max(0, category.spent - category.budget) : 0;

  const pillTone =
    pct <= 80
      ? "bg-[rgba(0,202,114,0.14)] text-[#00CA72]"
      : pct <= 100
        ? "bg-[rgba(255,204,0,0.14)] text-[#FFCC00]"
        : "bg-[rgba(251,39,93,0.14)] text-[#FB275D]";

  const title = isHe ? category.name : category.name;
  const budgetText = category.budget
    ? isHe
      ? `תקציב חודשי ${fmt(category.budget)}`
      : `Budget ${fmt(category.budget)}/mo`
    : isHe
      ? "ללא תקציב"
      : "No budget";

  const amountPrefix = category.type === "expense" ? "-" : "+";
  const amountClass = category.type === "income" ? "text-[var(--income)]" : "text-[var(--text-primary)]";
  const eyebrow =
    category.type === "expense"
      ? isHe
        ? "הוצאות · החודש"
        : "SPENT · THIS MONTH"
      : isHe
        ? "הכנסות · החודש"
        : "INCOME · THIS MONTH";

  return (
    <article
      className="flex flex-col gap-3 rounded-[14px] border border-[var(--border-color)] bg-[var(--bg-secondary)] px-5 py-[18px]"
      role="region"
      aria-label={isHe ? `קטגוריה ${title}` : `${title} category`}
    >
      <div className="flex items-center gap-3">
        <div
          className="grid h-10 w-10 place-items-center rounded-[10px] text-lg font-bold text-white"
          style={{ backgroundColor: category.color }}
        >
          {(title.charAt(0) || "?").toUpperCase()}
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-[14px] font-semibold text-[var(--text-primary)]">{title}</p>
          <p className="mt-[2px] truncate text-[11px] text-[var(--text-muted)]">{budgetText}</p>
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={onEdit}
            className="grid h-[26px] w-[26px] place-items-center rounded-[6px] text-[var(--text-muted)] transition-all duration-150 [transition-timing-function:cubic-bezier(0.4,0,0.2,1)] hover:bg-[var(--bg-deep)] hover:text-[var(--text-primary)]"
            aria-label={isHe ? `ערוך ${title}` : `Edit ${title}`}
          >
            <Edit2 className="h-[13px] w-[13px]" />
          </button>
          <button
            type="button"
            onClick={onMore}
            className="grid h-[26px] w-[26px] place-items-center rounded-[6px] text-[var(--text-muted)] transition-all duration-150 [transition-timing-function:cubic-bezier(0.4,0,0.2,1)] hover:bg-[var(--bg-deep)] hover:text-[var(--text-primary)]"
            aria-label={isHe ? `אפשרויות נוספות ל${title}` : `More options for ${title}`}
          >
            <MoreHorizontal className="h-[13px] w-[13px]" />
          </button>
        </div>
      </div>

      <button
        type="button"
        onClick={() => onOpen(category.name)}
        className="w-full text-start"
        aria-label={isHe ? `פתח ${title}` : `Open ${title}`}
      >
        <div className="flex items-center justify-between">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.08em] text-[var(--text-muted)]">
              {eyebrow}
            </p>
            <p
              className={clsx(
                "mt-[2px] text-[22px] font-bold [font-feature-settings:'tnum'] [font-variant-numeric:tabular-nums] [font-family:'JetBrains_Mono',monospace]",
                amountClass,
              )}
            >
              {amountPrefix}
              {fmt(category.spent)}
            </p>
          </div>
          {category.budget ? (
            <span
              className={clsx(
                "rounded-[100px] px-[10px] py-[3px] text-[11px] font-semibold [font-feature-settings:'tnum'] [font-variant-numeric:tabular-nums] [font-family:'JetBrains_Mono',monospace]",
                pillTone,
              )}
            >
              {pct}%
            </span>
          ) : null}
        </div>

        {category.budget ? (
          <>
            <div
              role="progressbar"
              aria-valuenow={Math.max(0, Math.min(100, pct))}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label={isHe ? `שימוש בתקציב ${title}` : `${title} budget usage`}
              className="mt-3 h-[5px] w-full overflow-hidden rounded-[100px] bg-[var(--bg-deep)]"
            >
              <div
                  className="h-full rounded-[100px] transition-all [transition-duration:400ms] [transition-timing-function:cubic-bezier(0.4,0,0.2,1)]"
                style={{ width: `${fillPct}%`, backgroundColor: overBudget ? "var(--expense)" : category.color }}
              />
            </div>
            <p className="mt-2 text-[11px] text-[var(--text-muted)] [font-feature-settings:'tnum'] [font-variant-numeric:tabular-nums] [font-family:'JetBrains_Mono',monospace]">
              {overBudget
                ? isHe
                  ? `${fmt(overAmount)} מעל התקציב`
                  : `${fmt(overAmount)} over budget`
                : isHe
                  ? `${fmt(leftAmount)} זמינים`
                  : `${fmt(leftAmount)} left`}
            </p>
          </>
        ) : null}
      </button>
    </article>
  );
}

